import { useState, useRef, useCallback, type DragEvent, type ChangeEvent } from 'react';

export interface FileDropZoneProps {
  /** Called when a file is selected or dropped */
  onFileSelect: (file: File) => void;
  /** Accepted MIME types, e.g. "image/*" or "video/*" */
  accept?: string;
  /** Current preview URL (from URL.createObjectURL or existing URL) */
  previewUrl?: string;
  /** Label shown inside the drop zone */
  label?: string;
  /** Hint text below the label */
  hint?: string;
  /** Extra class names */
  className?: string;
  /** Show as image preview (true) or generic file (false) */
  isImage?: boolean;
}

/**
 * Accessible drag-and-drop file upload zone.
 * Supports click-to-browse and keyboard navigation.
 * Swappable for real R2 signed uploads later — currently
 * uses URL.createObjectURL for local preview.
 */
export function FileDropZone({
  onFileSelect,
  accept = 'image/*',
  previewUrl,
  label = 'Drop file here or click to browse',
  hint = 'Supports JPG, PNG, WebP, and AVIF',
  className = '',
  isImage = true,
}: FileDropZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File) => {
      setError(null);

      // Validate MIME type if accept is specified
      if (accept && accept !== '*/*') {
        const acceptedTypes = accept.split(',').map((t) => t.trim());
        const matches = acceptedTypes.some((type) => {
          if (type.endsWith('/*')) {
            return file.type.startsWith(type.replace('/*', '/'));
          }
          return file.type === type;
        });
        if (!matches) {
          setError(`File type "${file.type}" is not supported. Accepted: ${accept}`);
          return;
        }
      }

      // Validate file size (max 50 MB)
      if (file.size > 50 * 1024 * 1024) {
        setError('File is too large. Maximum size is 50 MB.');
        return;
      }

      onFileSelect(file);
    },
    [accept, onFileSelect],
  );

  const onDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      // Reset so re-selecting the same file triggers onChange
      e.target.value = '';
    },
    [handleFile],
  );

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  }, []);

  return (
    <div className={className}>
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        aria-label={label}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={onKeyDown}
        className={`
          relative flex cursor-pointer flex-col items-center justify-center
          rounded-lg border-2 border-dashed p-6 transition-colors
          focus:outline-none focus:ring-2 focus:ring-primary/40
          ${dragOver
            ? 'border-primary bg-primary/5'
            : previewUrl
              ? 'border-soft/40 bg-bg'
              : 'border-soft/60 bg-bg hover:border-soft hover:bg-soft/10'
          }
        `.trim().replace(/\s+/g, ' ')}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onInputChange}
          className="sr-only"
          aria-hidden="true"
        />

        {previewUrl && isImage ? (
          <img
            src={previewUrl}
            alt="Upload preview"
            className="max-h-48 w-full rounded-md object-contain"
          />
        ) : previewUrl && !isImage ? (
          <div className="flex flex-col items-center gap-2">
            {/* Video placeholder icon */}
            <svg className="h-10 w-10 text-ink-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
            <span className="font-ui text-ui-xs text-ink-light">Video selected</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-center">
            {/* Upload icon */}
            <svg className="h-8 w-8 text-ink-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
            </svg>
            <span className="font-ui text-ui-sm font-medium text-ink">{label}</span>
            <span className="font-ui text-ui-xs text-ink-light">{hint}</span>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-2 font-ui text-ui-xs text-primary" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
